import React, { useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, CircularProgress,
  Alert, TextField, Grid, Chip, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PrintIcon from '@mui/icons-material/Print';
import { analizarAlbaran, crearRecambio, crearMovimiento, buscarRecambioPorReferencia } from '../api';
import { QRCodeSVG } from 'qrcode.react';

interface LineaDetectada {
  referencia: string | null;
  descripcion: string | null;
  cantidad: number;
  existe: any;
  precio_compra?: number;
  precio_venta?: number;
  categoria?: string;
  ubicacion_almacen?: string;
  confirmado?: boolean;
  seleccionado?: boolean;
}

export default function MegaScanPanel({ onProductosListos, onAbrirMovimiento, onAñadirCarrito, onSolicitarSalida, puedeDarEntrada, puedeSoloSolicitar }: {
  onProductosListos: () => void,
  onAbrirMovimiento: (recambio: any) => void,
  onAñadirCarrito: (recambio: any) => void,
  onSolicitarSalida: (recambio: any) => void,
  puedeDarEntrada: boolean,
  puedeSoloSolicitar: boolean
}) {
  const [scanResult, setScanResult] = useState<any>(null);
  const [scanning, setScanning] = useState(false);
  const [foto, setFoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analizando, setAnalizando] = useState(false);
  const [lineas, setLineas] = useState<LineaDetectada[]>([]);
  const [error, setError] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleSeleccionarFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFoto(file);
    setPreview(URL.createObjectURL(file));
    setLineas([]);
    setError('');
  };

  React.useEffect(() => {
    if (scanning) {
      import('html5-qrcode').then(({ Html5QrcodeScanner }) => {
        const scanner = new Html5QrcodeScanner('qr-reader-megascan', { fps: 10, qrbox: 250 }, false);
        scanner.render(
          async (decodedText: string) => {
            try {
              const recambio = await buscarRecambioPorReferencia(decodedText);
              setScanResult(recambio);
              setScanning(false);
              scanner.clear();
            } catch (e) {
              alert('Recambio no encontrado: ' + decodedText);
            }
          },
          () => {}
        );
        (window as any)._scannerMega = scanner;
      });
    } else {
      if ((window as any)._scannerMega) {
        try { (window as any)._scannerMega.clear(); } catch {}
      }
    }
  }, [scanning]);

  const handleAnalizar = async () => {
    if (!foto) return;
    setAnalizando(true);
    setError('');
    try {
      const result = await analizarAlbaran(foto);
      setLineas(result.lineas.map((l: any) => ({
        ...l,
        precio_compra: l.existe?.precio_compra || 0,
        precio_venta: l.existe?.precio_venta || 0,
        categoria: l.existe?.categoria || 'Otro',
        ubicacion_almacen: l.existe?.ubicacion_almacen || '',
        confirmado: false,
        seleccionado: true
      })));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error analizando el albarán');
    } finally {
      setAnalizando(false);
    }
  };

  const actualizarLinea = (index: number, campo: string, valor: any) => {
    setLineas(prev => prev.map((l, i) => i === index ? { ...l, [campo]: valor } : l));
  };

  const handleConfirmarTodo = async (soloSeleccionados: boolean) => {
    const lineasAConfirmar = soloSeleccionados ? lineas.filter(l => l.seleccionado) : lineas;
    if (lineasAConfirmar.length === 0) { alert('No hay productos seleccionados'); return; }
    setConfirmando(true);
    try {
      for (const linea of lineasAConfirmar) {
        if (linea.existe) {
          await crearMovimiento({
            recambio_id: linea.existe.id, tipo: 'entrada', cantidad: linea.cantidad,
            precio_unitario: linea.existe.precio_compra, motivo: 'Stock almacén', albaran: 'MegaScan'
          });
        } else {
          // El recambio nace a cero y la cantidad entra como movimiento,
          // para que el stock siempre sea consecuencia del histórico
          const creado = await crearRecambio({
            referencia: linea.referencia || `AUTO-${Date.now()}`,
            nombre: linea.descripcion || 'Sin descripción',
            categoria: linea.categoria, precio_compra: linea.precio_compra,
            precio_venta: linea.precio_venta, stock_actual: 0,
            stock_minimo: 1, ubicacion_almacen: linea.ubicacion_almacen
          });
          if (creado?.id && linea.cantidad > 0) {
            await crearMovimiento({
              recambio_id: creado.id, tipo: 'entrada', cantidad: linea.cantidad,
              precio_unitario: linea.precio_compra, motivo: 'Alta inicial MegaScan',
              albaran: 'MegaScan'
            });
          }
        }
      }
      alert(`✅ ${lineasAConfirmar.length} producto(s) procesados correctamente`);
      if (soloSeleccionados) {
        setLineas(prev => prev.filter(l => !l.seleccionado));
      } else {
        setLineas([]); setFoto(null); setPreview(null);
      }
      onProductosListos();
    } catch (e: any) {
      console.error('MegaScan:', e);
      alert('Error confirmando: ' + (e.response?.data?.detail || e.message || 'desconocido'));
      }
    finally { setConfirmando(false); }
  };

  const handleImprimirQRs = (soloSeleccionados: boolean) => {
    const lineasAImprimir = soloSeleccionados ? lineas.filter(l => l.seleccionado) : lineas;
    if (lineasAImprimir.length === 0) { alert('No hay productos seleccionados'); return; }
    const ventana = window.open('', '_blank');
    if (!ventana) return;
    let html = `<html><head><title>QRs MegaScan</title></head><body style="font-family:sans-serif">`;
    html += `<div style="display:flex;flex-wrap:wrap;gap:20px;">`;
    lineasAImprimir.forEach(l => {
      const ref = l.referencia || l.existe?.referencia || '';
      const nombre = l.descripcion || l.existe?.nombre || '';
      html += `<div style="text-align:center;width:200px;border:1px solid #ccc;padding:10px;margin-bottom:10px">
        <div id="qr-${ref}"></div>
        <p style="font-weight:bold;font-size:12px">${nombre}</p>
        <p style="font-size:10px;color:#666">${ref}</p>
      </div>`;
    });
    html += `</div></body></html>`;
    ventana.document.write(html);
    ventana.document.close();
    const script = ventana.document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
    script.onload = () => {
      lineasAImprimir.forEach(l => {
        const ref = l.referencia || l.existe?.referencia || '';
        const el = ventana.document.getElementById(`qr-${ref}`);
        if (el && (ventana as any).QRCode) new (ventana as any).QRCode(el, { text: ref, width: 120, height: 120 });
      });
      setTimeout(() => ventana.print(), 500);
    };
    ventana.document.body.appendChild(script);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>🪄 MegaScan</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>

        {/* Lector QR — siempre visible */}
        <Grid item xs={12} md={puedeDarEntrada ? 6 : 12}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>📷 Escanear QR</Typography>
              {!scanResult ? (
                <Box>
                  <Typography sx={{ mb: 2 }}>
                    Escanea el código QR pegado en el recambio físico para abrir su ficha directamente.
                  </Typography>
                  {!scanning ? (
                    <Button variant="contained" startIcon={<QrCodeScannerIcon />}
                      onClick={() => setScanning(true)} sx={{ bgcolor: '#4f46e5' }}>
                      Activar cámara
                    </Button>
                  ) : (
                    <Box>
                      <div id="qr-reader-megascan" style={{ width: '100%', maxWidth: 400 }}></div>
                      <Button onClick={() => setScanning(false)} sx={{ mt: 2 }}>Cancelar</Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">✅ Recambio encontrado</Typography>
                    <Button onClick={() => { setScanResult(null); setScanning(false); }}>Escanear otro</Button>
                  </Box>
                  <Typography sx={{ fontWeight: 600 }}>{scanResult.nombre}</Typography>
                  <Typography variant="caption" display="block">Ref: {scanResult.referencia}</Typography>
                  <Typography variant="caption" display="block">📍 {scanResult.ubicacion_almacen || '—'}</Typography>
                  <Chip
                    label={`Stock: ${scanResult.stock_actual}`}
                    color={scanResult.stock_actual <= scanResult.stock_minimo ? 'error' : 'success'}
                    sx={{ mt: 1, mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {puedeDarEntrada && (
                      <>
                        <Button variant="outlined" fullWidth onClick={() => onAbrirMovimiento(scanResult)}>
                          Movimiento
                        </Button>
                        <Button variant="contained" fullWidth sx={{ bgcolor: '#4f46e5' }}
                          onClick={() => { onAñadirCarrito(scanResult); setScanResult(null); }}>
                          🛒 Añadir a oferta
                        </Button>
                      </>
                    )}
                    {puedeSoloSolicitar && (
                      <Button variant="outlined" fullWidth
                        sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                        onClick={() => { onSolicitarSalida(scanResult); setScanResult(null); }}>
                        📋 Solicitar salida
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Lector albarán — solo si puede dar entrada */}
        {puedeDarEntrada && (
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>📋 Leer albarán</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Haz una foto al albarán de entrada y la IA detectará todos los productos automáticamente.
                </Typography>
                {!preview ? (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button variant="contained" startIcon={<CloudUploadIcon />}
                      onClick={() => fileInputRef.current?.click()} sx={{ bgcolor: '#4f46e5' }}>
                      Cargar albarán (PDF o imagen)
                    </Button>
                    <Button variant="contained" startIcon={<PhotoCameraIcon />}
                      onClick={() => cameraInputRef.current?.click()} sx={{ bgcolor: '#22c55e' }}>
                      Hacer foto ahora
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <img src={preview} alt="Albarán" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, marginBottom: 16 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" onClick={() => { setFoto(null); setPreview(null); setLineas([]); }}>
                        Cambiar albarán
                      </Button>
                      {lineas.length === 0 && (
                        <Button variant="contained" onClick={handleAnalizar} disabled={analizando} sx={{ bgcolor: '#4f46e5' }}>
                          {analizando ? <CircularProgress size={20} color="inherit" /> : '🔍 Analizar con IA'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
                <input ref={fileInputRef} type="file" accept="image/*,.pdf" hidden onChange={handleSeleccionarFoto} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handleSeleccionarFoto} />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>

      {/* Líneas detectadas — solo si puede dar entrada */}
      {puedeDarEntrada && lineas.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>📋 {lineas.length} producto(s) detectado(s)</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {lineas.map((linea, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card sx={{ borderRadius: 3, borderLeft: linea.existe ? '4px solid #22c55e' : '4px solid #f59e0b' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <input type="checkbox" checked={!!linea.seleccionado}
                          onChange={(e) => actualizarLinea(i, 'seleccionado', e.target.checked)}
                          style={{ marginTop: 4 }} />
                        <Typography sx={{ fontWeight: 600 }}>{linea.descripcion || linea.existe?.nombre || 'Sin descripción'}</Typography>
                      </Box>
                      <Chip label={linea.existe ? 'Existente' : 'Nuevo'} color={linea.existe ? 'success' : 'warning'} size="small" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ref: {linea.referencia || '—'} | Cantidad: {linea.cantidad}
                    </Typography>
                    {!linea.existe && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField size="small" label="Referencia" value={linea.referencia || ''}
                          onChange={(e) => actualizarLinea(i, 'referencia', e.target.value)} />
                        <TextField size="small" label="Nombre" value={linea.descripcion || ''}
                          onChange={(e) => actualizarLinea(i, 'descripcion', e.target.value)} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField size="small" label="Precio compra" type="number" value={linea.precio_compra}
                            onChange={(e) => actualizarLinea(i, 'precio_compra', parseFloat(e.target.value) || 0)} />
                          <TextField size="small" label="Precio venta" type="number" value={linea.precio_venta}
                            onChange={(e) => actualizarLinea(i, 'precio_venta', parseFloat(e.target.value) || 0)} />
                        </Box>
                        <TextField size="small" label="Ubicación" value={linea.ubicacion_almacen}
                          onChange={(e) => actualizarLinea(i, 'ubicacion_almacen', e.target.value)} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => handleConfirmarTodo(false)} disabled={confirmando} sx={{ bgcolor: '#22c55e' }}>
              {confirmando ? <CircularProgress size={20} color="inherit" /> : '✅ Confirmar todas las entradas'}
            </Button>
            <Button variant="contained" onClick={() => handleConfirmarTodo(true)} disabled={confirmando} sx={{ bgcolor: '#16a34a' }}>
              ✅ Confirmar seleccionados ({lineas.filter(l => l.seleccionado).length})
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => handleImprimirQRs(false)}>
              🖨️ Imprimir todos los QR
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => handleImprimirQRs(true)}>
              🖨️ Imprimir seleccionados ({lineas.filter(l => l.seleccionado).length})
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}